import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { supabase } from '../config/supabase';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Try to fetch from news table first
      let { data: newsData, error: newsError } = await supabase
        .from('news')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (!newsError && newsData) {
        setPost({
          ...newsData,
          type: 'news',
          date: new Date(newsData.published_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          image: newsData.image_url,
          author: newsData.author_name || 'IBV Team'
        });
        setLoading(false);
        return;
      }

      // If not found in news, try events table
      let { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (!eventError && eventData) {
        setPost({
          ...eventData,
          type: 'event',
          date: new Date(eventData.event_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          image: eventData.image_url,
          author: eventData.author_name || 'IBV Team'
        });
        setLoading(false);
        return;
      }

      // If not found in database, show error
      setError('Content not found');
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !post) {
    return (
      <div className="page-container">
        <div className="page-wrapper" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <i className="fas fa-exclamation-circle" style={{ fontSize: '64px', color: '#ddd', marginBottom: '20px' }}></i>
          <h2 style={{ marginBottom: '12px' }}>{error || 'Content Not Found'}</h2>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            The content you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/news')}
            style={{
              padding: '12px 24px',
              background: '#CDB688',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-arrow-left" style={{marginRight: '8px'}}></i>
            Back to News
          </button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-wrapper">
        <button 
          onClick={() => navigate('/news')} 
          style={{
            background: 'none',
            border: 'none',
            color: '#CDB688',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>

        <div className="blog-tag" style={{marginBottom: '12px'}}>{post.category}</div>
        
        <h1 className="page-title" style={{marginBottom: '12px'}}>{post.title}</h1>
        
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '24px',
          fontSize: '14px',
          color: '#666',
          flexWrap: 'wrap'
        }}>
          <span><i className="far fa-calendar"></i> {post.date}</span>
          {post.location && <span><i className="fas fa-map-marker-alt"></i> {post.location}</span>}
          <span><i className="far fa-user"></i> {post.author}</span>
        </div>

        <img 
          src={post.image} 
          alt={post.title}
          style={{
            width: '100%',
            height: '300px',
            objectFit: 'cover',
            borderRadius: '16px',
            marginBottom: '32px'
          }}
        />

        {post.excerpt && (
          <div style={{
            background: '#f9f9f9',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '24px',
            borderLeft: '4px solid #CDB688'
          }}>
            <p style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: '#666',
              fontStyle: 'italic',
              margin: 0
            }}>
              {post.excerpt}
            </p>
          </div>
        )}

        <div 
          className="blog-post-content"
          style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#333',
            whiteSpace: 'pre-wrap'
          }}
        >
          {post.content}
        </div>

        <div style={{
          marginTop: '48px',
          paddingTop: '24px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: '12px'
        }}>
          <button 
            onClick={() => navigate('/news')} 
            className="btn btn-primary"
            style={{
              padding: '12px 24px',
              background: '#CDB688',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <i className="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default BlogPost;
